import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { useLanguage } from "../context/LanguageProvider";
import { LiaUserSlashSolid } from "react-icons/lia";
import { MdOutlineDeliveryDining as Moto } from "react-icons/md";
import { appConfig } from '../apiConfig';
import { useTheme } from '../context/ThemeProvider';
import {
  Menu,
  Home,
  Layers,
  Tag,
  Shapes,
  MapPin,
  Settings,
  ShieldCheck,
  Headset,
  Users,
  ShoppingCart,
  ChevronDown,
  ChevronUp,
  Warehouse,
  Contact,
  CreditCard,
  MessageCircleWarning,
  Megaphone,
  Users2,
  FilePlus2,
  HelpCircle,
  Truck,
  Undo2,
  XCircle,
  Ban,
  PackageCheck,
  ScanLine,
  ClipboardList,
  CircleCheckBig,
  Link as Integration,
  MonitorCog,
  UserRoundCog,
  LayoutGrid,
  Crown,
  Lock,
  ListChecks,
  Terminal,
  X,
} from "lucide-react";

function PremiumCard({ t }) {
  return (
    <div className="mx-4 mb-2 rounded-xl bg-amber-50 py-2 px-4">
      <div className="flex items-center gap-2 text-amber-400 text-[12px] font-semibold mb-1.5">
        <Crown className="h-4 w-4" />
        {t ? t('navbar.go_premium') : "Passez à Premium"}
      </div>
      <p className="text-[9px] text-slate-400 mb-2 leading-relaxed">
        {t ? t('navbar.go_premium_desc') : "Débloquez plus de fonctionnalités et boostez votre business."}
      </p>
      <button className="w-full flex items-center justify-center gap-1 rounded-lg bg-amber-400 hover:bg-amber-500 transition-colors text-white text-[10px] font-semibold py-2">
        <Lock className="h-3 w-3" />
        {t ? t('navbar.discover_premium') : "Découvrir Premium"}
      </button>
    </div>
  );
}

export default function Navbar({ open, setOpen }) {
  const { role, roles } = useAuth();
  const { t , locale } = useLanguage();
  const [activeSection, setActiveSection] = useState(null);
  const location = useLocation();
  const pathname = location.pathname;
  const { isDark } = useTheme();

  const handleLinkClick = () => {
    if (window.innerWidth < 1024) {
      setOpen(false);
    }
  };

  const navigation = [
    {
      title: t('navbar.section_general'),
      items: [
        { name: t('navbar.dashboard'), link: "/dashboard", icon: Home, badge: null, role: [0, 1, 2, 3, 4] },
        {
          name: t('navbar.orders'),
          icon: ShoppingCart,
          subItems: [
            { name: t('navbar.all_orders'), link: "/orders/tout", icon: ClipboardList, badge: null },
            { name: t('navbar.new_order'), link: "/orders/nouveau", icon: FilePlus2, badge: null },
            { name: t('navbar.delivered'), link: "/orders/livre", icon: PackageCheck, badge: null },
            { name: t('navbar.confirmed'), link: "/orders/confirme", icon: CircleCheckBig, badge: null },
            { name: t('navbar.waiting_confirmation'), link: "/orders/reporte", icon: HelpCircle, badge: null },
            { name: t('navbar.in_follow_up'), link: "/orders/suivi", icon: Headset, badge: null },
            { name: t('navbar.in_delivery'), link: "/orders/indelivery", icon: Truck, badge: null },
            { name: t('navbar.returned'), link: "/orders/retour", icon: Undo2, badge: null },
            { name: t('navbar.refused'), link: "/orders/refuse", icon: XCircle, badge: null },
            { name: t('navbar.cancelled'), link: "/orders/annule", icon: Ban, badge: null },
          ],
          role: roles?.view_orders === 1 ? [0, 1, 2, 3, 4] : [0, 1, 3, 4],
        },
      ],
    },
    {
      title: role == 0 || role == 1 || role == 2 || role == 3 ? t('navbar.section_operations') : null,
      items: [
        { name: t('navbar.products'), link: "/products", icon: Tag, badge: null, role: [0] },
        { name: t('navbar.categories'), link: "/categories", icon: Layers, badge: null, role: [0] },
        {
          name: t('navbar.stock_management'),
          icon: Warehouse,
          subItems: [
            { name: t('navbar.products'), link: "/products", icon: Tag, badge: null, role: roles?.view_products === 1 ? [1, 2] : [1] },
            { name: t('navbar.variants'), link: "/variants", icon: Shapes, badge: null, role: roles?.view_products === 1 ? [1, 2] : [1] },
            { name: t('navbar.categories'), link: "/categories", icon: Layers, badge: null, role: roles?.view_products === 1 ? [1, 2] : [1] },
          ],
          role: roles?.view_products === 1 || roles?.view_variants === 1 || roles?.view_categories === 1 ? [1, 2] : [1],
        },
        { name: t('navbar.cities'), link: "/cities", icon: MapPin, badge: null, role: [0, 1, 2, 3] },
        {
          name: t('navbar.scan'),
          icon: ScanLine,
          subItems: [
            { name: t('navbar.scan_ramassage'), link: "/scan-ramassage", icon: ListChecks, badge: null, role: roles?.scan_order === 1 ? [1, 2] : [1] },
            { name: t('navbar.scan_return'), link: "/scan-retour", icon: Undo2, badge: null, role: roles?.scan_order === 1 ? [1, 2] : [1] },
          ],
          role: roles?.scan_order === 1 ? [1, 2] : [1],
        },
      ],
    },
    {
      title: 
        (role == 0 || role == 1 || role == 3) 
        || (role == 2 && (roles?.view_saller === 1 || roles?.view_confirmator === 1 || roles?.view_fournisseurs === 1 || roles?.view_blacklist === 1)) 
        ? t('navbar.section_team') : null,
      items: [
        {
          name: t('navbar.team'),
          icon: Users2,
          subItems: [
            { name: t('navbar.admins'), link: "/admins", icon: ShieldCheck, badge: null, role: [1] },
            { name: t('navbar.sellers'), link: "/sallers", icon: Users, badge: null, role: roles?.view_saller === 1 ? [1, 2] : [1] },
            { name: t('navbar.confirmators'), link: "/confirmators", icon: Headset, badge: null, role: roles?.view_confirmator === 1 ? [1, 2] : [1] },
            { name: t('navbar.livreur'), link: "/livreurs", icon: Moto, badge: null, role: roles?.view_confirmator === 1 ? [1, 2] : [1] },
            { name: t('navbar.suppliers'), link: "/fournisseurs", icon: Contact, badge: null, role: roles?.view_fournisseurs === 1 ? [1, 2] : [1] },
            { name: t('navbar.roles'), link: "/roles", icon: UserRoundCog, badge: null, role: [1] },
            { name: t('navbar.black_list'), link: "/black-list", icon: LiaUserSlashSolid, badge: null, role:roles?.view_blacklist === 1 ? [1, 2] : [1] },
          ],
          role: roles?.view_saller === 1 || roles?.view_confirmator === 1 || roles?.view_fournisseurs === 1 || roles?.view_blacklist === 1 ? [1, 2] : [1],
        },
        { name: t('navbar.payments_simple'), link: "/payments", icon: CreditCard, badge: null, role: [0, 3] },
        {
          name: t('navbar.payments'),
          icon: CreditCard,
          subItems: [
            { name: t('navbar.sallers'), link: "/payments/sallers", icon: Users, badge: null, role: roles?.view_saller_invoices === 1 ? [1, 2] : [1] },
            { name: t('navbar.confirmators_pay'), link: "/payments/confirmators", icon: Headset, badge: null, role: roles?.view_confirmator_invoices === 1 ? [1, 2] : [1] },
            { name: t('navbar.system'), link: "/payments/system", icon: MonitorCog, badge: null, role: 1 },
          ],
          role: roles?.view_saller_invoices === 1 || roles?.view_confirmator_invoices === 1 ? [1, 2] : [1]
        },
        { name: t('navbar.delivery'), link: "/integration/delivery", icon: Truck, badge: null, role: [1] },
        { name: t('navbar.applications'), link: "/integration/applications", icon: LayoutGrid, badge: null , role: appConfig.slug === 'nextlead' ? [0] : [9] },
        { name: t('navbar.api_docs'), link: "/integration/apis", icon: Terminal, badge: null, role: [0] },
      ],
    },
    {
      title: 
      (role == 0 || role == 1 || role == 3)
      || (role == 2 && (roles?.view_reclamations === 1 || roles?.edit_config === 1 || roles?.view_annonces === 1)) ? 
      t('navbar.section_settings') : null,
      items: [
        { name: t('navbar.complaints'), link: "/reclamations", icon: MessageCircleWarning, badge: null, role: roles?.view_reclamations === 1 ? [0, 1, 2, 3] : [0, 1, 3] },
        { name: t('navbar.configurations'), link: "/configs", icon: Settings, badge: null, role: roles?.edit_config === 1 ? [1, 2] : [1] },
        { name: t('navbar.announcements'), link: "/annonces", icon: Megaphone, badge: null, role: roles?.view_annonces === 1 ? [1, 2] : [1] },
        { name: t('navbar.activity_logs'), link: "/activity-logs", icon: Terminal, badge: null, role: [1] },
      ],
    },
  ];

  const isActive = (link) => pathname === link;

  const toggleSection = (sectionName) => {
    setActiveSection(activeSection === sectionName ? null : sectionName);
  };

  const hasAccess = (item) => {
    if (item.permissionCheck) {
      return item.permissionCheck();
    }

    const itemRole = item.role;
    if (!itemRole) return true;
    if (Array.isArray(itemRole)) {
      return itemRole.includes(role);
    }
    return role === itemRole;
  };

  const renderNavItem = (item) => {
    if (!hasAccess(item)) return null;

    if (item.subItems) {
      const isOpen = activeSection === item.name;

      return (
        <div key={item.name} className="space-y-1">
          <button
            onClick={() => toggleSection(item.name)}
            className={`group cursor-pointer flex items-center justify-between w-full px-3 py-2.5 text-[10px]
                        hover:bg-[var(--color-primary-50)] hover:text-[var(--color-primary-500)] hover:shadow-sm font-medium text-gray-600
                        rounded-xl transition-all duration-500
                        ${isOpen ? "text-[var(--color-primary-500)]" : "text-gray-600"}`}
          >
            <div className="flex items-center gap-3 whitespace-nowrap">
              <item.icon
                className={`h-4 w-4 ${
                  isOpen ? "text-[var(--color-primary-500)]" : "text-gray-400 group-hover:text-[var(--color-primary-500)]"
                }`}
              />
              {open && <span>{item.name}</span>}
            </div>
            {open && (isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />)}
          </button>

          {isOpen && open && (
            <div className={`${locale === "ar" ? 'mr-6' : 'ml-8'} space-y-0.5`}>
              {item.subItems
                .filter((subItem) => hasAccess(subItem))
                .map((subItem) => (
                  <Link
                    key={subItem.name}
                    to={subItem.link}
                    onClick={handleLinkClick}
                    className={`group flex items-center justify-between px-3 py-2 text-[10px] font-medium rounded-xl border
                               transition-all duration-200 hover:shadow-sm
                               ${
                                 isActive(subItem.link)
                                   ? "bg-[var(--color-primary-50)] border-[var(--color-primary-500)] text-[var(--color-primary-500)]"
                                   : "border-transparent text-gray-600 hover:bg-[var(--color-primary-50)] hover:text-[var(--color-primary-500)]"
                               }`}
                  >
                    <div className="flex gap-3 items-center whitespace-nowrap">
                      <subItem.icon
                        className={`h-4 w-4 ${
                          isActive(subItem.link)
                            ? "text-[var(--color-primary-500)]"
                            : "text-gray-400 group-hover:text-[var(--color-primary-500)]"
                        }`}
                      />
                      <span>{subItem.name}</span>
                    </div>
                    {subItem.badge && (
                      <span className="bg-[#d1f0ec] text-[var(--color-primary-500)] text-[10px] px-2 py-1 rounded-full">
                        {subItem.badge}
                      </span>
                    )}
                  </Link>
                ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.name}
        to={item.link}
        onClick={handleLinkClick}
        className={`group flex items-center justify-between px-3 py-2.5 text-[10px] font-medium rounded-xl border
                   transition-all duration-200 hover:shadow-sm
                   ${
                     isActive(item.link)
                       ? "bg-[var(--color-primary-50)] border-[var(--color-primary-500)] text-[var(--color-primary-500)]"
                       : "border-transparent text-gray-600 hover:bg-[var(--color-primary-50)] hover:text-[var(--color-primary-500)]"
                   }`}
      >
        <div className="flex gap-3 items-center whitespace-nowrap">
          <item.icon
            className={`h-4 w-4 ${
              isActive(item.link) ? "text-[var(--color-primary-500)]" : "text-gray-400 group-hover:text-[var(--color-primary-500)]"
            }`}
          />
          {open && <span>{item.name}</span>}
        </div>
        {open && item.badge && (
          <span className="bg-[#d1f0ec] text-[var(--color-primary-500)] text-[10px] px-2 py-1 rounded-full">
            {item.badge}
          </span>
        )}
      </Link>
    );
  };

  return (
    <>
      <nav
        className={`
          ${open ? "w-[18%]" : "max-lg:w-0 w-20"}
          max-lg:fixed max-lg:top-[9vh] max-lg:left-0 max-lg:h-[91vh] max-lg:z-[99]
          max-lg:bg-white max-lg:shadow-xl
          ${open ? "max-lg:w-64" : "max-lg:w-0 max-lg:overflow-hidden"}
          transition-all duration-300 ease-in-out
          lg:sticky lg:top-0 lg:bottom-0 lg:left-0 lg:h-screen
          bg-white shadow-xl md:shadow-none dark:shadow-none`}
      >
        <div className={` ${open ? "pt-4" : "pt-0"} w-full flex justify-center`}>
          <Link to="/dashboard" className={`${!open ? "mt-2" : ""} flex items-center max-lg:w-[30%] cursor-pointer max-lg:hidden` }>
            <img
                src={open ? (isDark ? `/images/${appConfig.slug}-logo-whitee.png` : `/images/${appConfig.slug}-logo.png`) : isDark ? `/images/${appConfig.slug}-logo-solo.png` : `/images/${appConfig.slug}-fav.png`}
                alt={appConfig.name}
                width={open ? 170 : 55}
                height={100}
              />
            </Link>
            
        </div>
        <div className="max-lg:hidden absolute bottom-0 left-0 right-0 lg:flex flex-col h-[91vh] pt-0 pb-0">
          <div className="flex-1 px-4 space-y-0.5 overflow-y-auto custom-scrollbar">
            {navigation.map((section) => (
              <div key={section.title}>
                {open && (
                  <div className="px-3 pt-2 pb-1 text-[9px] font-bold uppercase tracking-widest text-slate-400">
                    {section.title}
                  </div>
                )}
                {section.items.map((item) => renderNavItem(item))}
              </div>
            ))}
          </div>
          {open && <PremiumCard t={t} />}
        </div>

        <div className="lg:hidden flex flex-col h-full pt-0">
          <div className="flex-1 px-4 pt-5 space-y-1.5 overflow-y-auto custom-scrollbar">
            {navigation.map((section) => (
              <div key={section.title}>
                <div className="px-3 pt-4 pb-1 text-[9px] font-bold uppercase tracking-widest text-slate-400">
                  {section.title}
                </div>
                {section.items.map((item) => renderNavItem(item))}
              </div>
            ))}
          </div>
          <div className="pb-6">
            <PremiumCard t={t} />
          </div>
        </div>
      </nav>

      <button
        className="lg:hidden fixed bottom-6 left-6 z-[100] p-3 rounded-full bg-teal-500 text-[#0B2E2E] shadow-lg"
        onClick={() => setOpen(!open)}
        aria-label={open ? "Close menu" : "Open menu"}
      >
        {open ? <X className="h-6 w-6 text-white" /> : <Menu className="h-6 w-6 text-white" />}
      </button>
    </>
  );
}